import React from 'react';
import { ResponsiveECharts } from './ResponsiveECharts';
import { ChartCard } from '../ChartCard';
import '../studysyncEChartsTheme'; // side effect: registers the 'studysync' theme before render
import { tokens } from '../tokens';
import { ECHARTS_ANIM } from '../motion';
import type { ChartSpec, ChartData } from '../copilotTypes';

type StackedAreaSpec = Extract<ChartSpec, { chart: 'stacked_area' }>;
type StackedAreaData = Extract<ChartData, { chart: 'stacked_area' }>;

// Sleep stages are stacked bottom→top in physiological order: the deepest stage anchors the bottom of
// each night's column and lighter stages layer above, with Awake on top. Each stage's minutes come
// straight from the fixture; the series colors are pulled from the registered 'studysync' theme
// palette (do NOT hardcode) so the four bands stay on-brand and distinct.
const STAGES = [
  { key: 'deep', name: 'Deep' },
  { key: 'core', name: 'Core' },
  { key: 'rem', name: 'REM' },
  { key: 'awake', name: 'Awake' },
] as const;

// Every stage is measured in minutes; surface the unit in the tooltip so the values are unambiguous.
const UNIT = 'min';

export function buildStackedAreaOption(_spec: StackedAreaSpec, data: StackedAreaData) {
  const dates = data.nights.map((n) => n.date);
  const names = STAGES.map((s) => s.name);

  return {
    ...ECHARTS_ANIM,
    // Extra right padding so the final date label (e.g. "2026-05") isn't clipped at the grid's right
    // edge. With boundaryGap:false the last category sits exactly on the boundary, so alignMaxLabel on
    // the axis anchors that label's right side inward while the padding gives it room.
    grid: { left: 4, right: 28, top: 40, bottom: 24, containLabel: true },
    // Legend sits top-right so it clears the y-axis unit ('min'), which renders at the axis 'end' in the
    // top-left corner — keeping them on opposite sides avoids the two texts overlapping.
    legend: {
      data: names,
      top: 0,
      right: 0,
      itemWidth: 12,
      itemHeight: 12,
      icon: 'roundRect',
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line' },
      // Two-line tooltip mirrors the histogram/calendar style: a header line naming the bucket (the
      // night's date), then one bold value per stage with its 'min' unit so every band is readable.
      formatter: (params: any) => {
        const rows = Array.isArray(params) ? params : [params];
        const header = rows[0]?.axisValue ?? '';
        const lines = rows
          .map(
            (p: any) =>
              `${p.marker}${p.seriesName} <strong>${(Number(p.value) || 0).toLocaleString()}</strong> ${UNIT}`,
          )
          .join('<br/>');
        return `${header}<br/>${lines}`;
      },
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: dates,
      // Anchor the first/last labels inward so neither the earliest nor latest date clips against the
      // grid edge in a narrow panel (boundaryGap:false puts both exactly on the boundary).
      axisLabel: { hideOverlap: true, alignMinLabel: 'left', alignMaxLabel: 'right' },
    },
    yAxis: {
      type: 'value',
      name: UNIT,
      nameTextStyle: { color: tokens.text.secondary, fontFamily: tokens.fontFamily, fontSize: 12 },
    },
    series: STAGES.map((stage) => ({
      name: stage.name,
      type: 'line',
      stack: 'sleep',
      areaStyle: {},
      showSymbol: false,
      emphasis: { focus: 'series' },
      data: data.nights.map((n) => n[stage.key]),
    })),
  };
}

// memoized so a streaming re-render doesn't rebuild the option -> setOption(notMerge) reflow (see Histogram).
export const StackedArea = React.memo(function StackedArea({ title, spec, data }: { title: string; spec: StackedAreaSpec; data: StackedAreaData }) {
  const option = React.useMemo(() => buildStackedAreaOption(spec, data), [spec, data]);
  return (
    <ChartCard title={title} downloadable>
      <ResponsiveECharts theme="studysync" option={option} style={{ height: 280 }} notMerge />
    </ChartCard>
  );
});
